import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { X, Calendar, AlignLeft, Trash2, Clock, ListChecks, MessageSquare, Send } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITIES = [
  { value: "low",    label: "Low",    color: "#3b82f6", bg: "rgba(59,130,246,0.12)",  border: "rgba(59,130,246,0.35)"  },
  { value: "medium", label: "Medium", color: "#6b7280", bg: "rgba(107,114,128,0.1)",  border: "rgba(107,114,128,0.3)"  },
  { value: "high",   label: "High",   color: "#d97706", bg: "rgba(217,119,6,0.12)",   border: "rgba(217,119,6,0.35)"   },
  { value: "urgent", label: "Urgent", color: "#dc2626", bg: "rgba(220,38,38,0.12)",   border: "rgba(220,38,38,0.35)"   },
];

const LABEL = {
  display: "block",
  fontFamily: "'Manrope', sans-serif",
  color: "#374151",
  fontSize: "0.78rem",
  fontWeight: 700,
  marginBottom: "0.35rem",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const INPUT = {
  width: "100%",
  background: "rgba(255,255,255,0.6)",
  border: "1px solid rgba(26,77,46,0.18)",
  borderRadius: "0.5rem",
  padding: "0.6rem 0.875rem",
  color: "#0F172A",
  fontSize: "0.9rem",
  fontFamily: "'Manrope', sans-serif",
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 0.2s",
};

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric", month: "long", day: "numeric",
  });
}

export function dueDateStatus(dueDateStr) {
  if (!dueDateStr) return null;
  const due = new Date(dueDateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  const diff = due - today;
  if (diff < 0) return { label: "Overdue", color: "#dc2626", bg: "rgba(220,38,38,0.1)", border: "rgba(220,38,38,0.3)" };
  if (diff === 0) return { label: "Due today", color: "#d97706", bg: "rgba(217,119,6,0.1)", border: "rgba(217,119,6,0.3)" };
  return {
    label: `Due ${due.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`,
    color: "#3d6b1f", bg: "rgba(61,107,31,0.08)", border: "rgba(61,107,31,0.25)",
  };
}

export default function TaskDetailModal({ task, token, onClose, onUpdated, onDeleted }) {
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description || "");
  const [dueDate, setDueDate] = useState(task.due_date ? task.due_date.substring(0, 10) : "");
  const [priority, setPriority] = useState(task.priority || "medium");
  const [checklist, setChecklist] = useState(task.checklist || []);
  const [newItem, setNewItem] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Comments state
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [postingComment, setPostingComment] = useState(false);
  const commentsEndRef = useRef(null);

  const authHeaders = { Authorization: `Bearer ${token}` };

  // Load comments on mount
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/tasks/${task.id}/comments`, { headers: authHeaders })
      .then(({ data }) => setComments(data))
      .catch((err) => console.error("load comments failed:", err));
  }, [task.id]); // eslint-disable-line

  const scrollToBottom = () => {
    commentsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handlePostComment = async () => {
    const text = commentText.trim();
    if (!text) return;
    setPostingComment(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/tasks/${task.id}/comments`,
        { text },
        { headers: authHeaders }
      );
      setComments((prev) => [...prev, data]);
      setCommentText("");
      setTimeout(scrollToBottom, 50);
    } catch (err) { console.error("post comment failed:", err); }
    finally { setPostingComment(false); }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/comments/${commentId}`, { headers: authHeaders });
      setComments((prev) => prev.filter((c) => c.id !== commentId));
    } catch (err) { console.error("delete comment failed:", err); }
  };

  const saveChecklistNow = async (newList) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/tasks/${task.id}`, { checklist: newList }, { headers: authHeaders });
    } catch (err) { console.error("save checklist failed:", err); }
  };

  const addChecklistItem = () => {
    const text = newItem.trim();
    if (!text) return;
    const item = { id: Date.now().toString(), text, done: false };
    const updated = [...checklist, item];
    setChecklist(updated);
    setNewItem("");
    saveChecklistNow(updated);
  };

  const toggleChecklistItem = (id) => {
    const updated = checklist.map((i) => i.id === id ? { ...i, done: !i.done } : i);
    setChecklist(updated);
    saveChecklistNow(updated);
  };

  const deleteChecklistItem = (id) => {
    const updated = checklist.filter((i) => i.id !== id);
    setChecklist(updated);
    saveChecklistNow(updated);
  };

  const handleSave = async () => {
    if (!title.trim()) { setError("Title cannot be empty."); return; }
    setSaving(true);
    setError("");
    try {
      const { data } = await axios.patch(
        `${BACKEND_URL}/api/tasks/${task.id}`,
        { title: title.trim(), description: description || null, due_date: dueDate || null, priority, checklist },
        { headers: authHeaders }
      );
      onUpdated?.(data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save changes.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/tasks/${task.id}`, { headers: authHeaders });
      onDeleted?.(task.id);
      onClose();
    } catch {
      setError("Failed to delete task.");
    }
  };

  return (
    <div
      data-testid="task-detail-overlay"
      style={{
        position: "fixed", inset: 0, zIndex: 70,
        background: "rgba(0,0,0,0.35)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "1rem",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        data-testid="task-detail-modal"
        style={{
          background: "rgba(255,255,255,0.95)",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          border: "1px solid rgba(255,255,255,0.5)",
          borderRadius: "1rem",
          boxShadow: "0 12px 48px rgba(26,77,46,0.16)",
          width: "100%", maxWidth: "580px",
          padding: "1.5rem",
          position: "relative",
          animation: "fadeInDown 0.2s ease",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        {/* Close button */}
        <button
          data-testid="task-detail-close-btn"
          onClick={onClose}
          style={{
            position: "absolute", top: "1rem", right: "1rem",
            background: "rgba(26,77,46,0.06)", border: "1px solid rgba(26,77,46,0.15)",
            borderRadius: "50%", width: "30px", height: "30px",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", color: "#6b7280",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(26,77,46,0.12)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(26,77,46,0.06)"; }}
        >
          <X size={14} />
        </button>

        {/* Title */}
        <div style={{ marginBottom: "1.25rem" }}>
          <label style={LABEL}>Title</label>
          <input
            data-testid="task-detail-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={{ ...INPUT, fontSize: "1rem", fontWeight: 600, color: "#1A4D2E" }}
            onFocus={(e) => (e.target.style.borderColor = "#3d6b1f")}
            onBlur={(e) => (e.target.style.borderColor = "rgba(26,77,46,0.18)")}
          />
        </div>

        {/* Priority */}
        <div style={{ marginBottom: "1.25rem" }}>
          <label style={LABEL}>Priority</label>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            {PRIORITIES.map((p) => {
              const active = priority === p.value;
              return (
                <button
                  key={p.value}
                  data-testid={`priority-btn-${p.value}`}
                  onClick={() => setPriority(p.value)}
                  style={{
                    padding: "0.3rem 0.875rem",
                    borderRadius: "2rem",
                    border: `1.5px solid ${active ? p.border : "rgba(26,77,46,0.12)"}`,
                    background: active ? p.bg : "rgba(255,255,255,0.5)",
                    color: active ? p.color : "#9ca3af",
                    fontFamily: "'Manrope', sans-serif",
                    fontSize: "0.8rem", fontWeight: active ? 700 : 500,
                    cursor: "pointer",
                    transition: "all 0.15s",
                  }}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Description */}
        <div style={{ marginBottom: "1.25rem" }}>
          <label style={{ ...LABEL, display: "flex", alignItems: "center", gap: "0.3rem" }}>
            <AlignLeft size={12} /> Description
          </label>
          <textarea
            data-testid="task-detail-description-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Add a description…"
            rows={3}
            style={{
              ...INPUT,
              resize: "vertical",
              minHeight: "72px",
              fontFamily: "'Manrope', sans-serif",
              lineHeight: 1.5,
            }}
            onFocus={(e) => (e.target.style.borderColor = "#3d6b1f")}
            onBlur={(e) => (e.target.style.borderColor = "rgba(26,77,46,0.18)")}
          />
        </div>

        {/* Due Date */}
        <div style={{ marginBottom: "1.25rem" }}>
          <label style={{ ...LABEL, display: "flex", alignItems: "center", gap: "0.3rem" }}>
            <Calendar size={12} /> Due Date
          </label>
          <input
            data-testid="task-detail-due-date-input"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            style={{ ...INPUT, width: "auto", minWidth: "180px" }}
            onFocus={(e) => (e.target.style.borderColor = "#3d6b1f")}
            onBlur={(e) => (e.target.style.borderColor = "rgba(26,77,46,0.18)")}
          />
          {dueDate && (
            <button
              onClick={() => setDueDate("")}
              style={{
                marginLeft: "0.5rem", background: "none", border: "none",
                color: "#9ca3af", cursor: "pointer", fontSize: "0.75rem",
                fontFamily: "'Manrope', sans-serif",
              }}
            >
              clear
            </button>
          )}
        </div>

        {/* Checklist */}
        <div style={{ marginBottom: "1.25rem" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.5rem" }}>
            <label style={{ ...LABEL, display: "flex", alignItems: "center", gap: "0.3rem", margin: 0 }}>
              <ListChecks size={12} /> Checklist
            </label>
            {checklist.length > 0 && (
              <span style={{ fontFamily: "'Manrope', sans-serif", color: "#6b7280", fontSize: "0.75rem", fontWeight: 600 }}>
                {checklist.filter((i) => i.done).length} of {checklist.length} done
              </span>
            )}
          </div>

          {/* Progress bar */}
          {checklist.length > 0 && (
            <div style={{ height: "4px", background: "rgba(26,77,46,0.1)", borderRadius: "2px", marginBottom: "0.75rem", overflow: "hidden" }}>
              <div style={{
                height: "100%",
                width: `${Math.round((checklist.filter((i) => i.done).length / checklist.length) * 100)}%`,
                background: checklist.every((i) => i.done) ? "#3d6b1f" : "linear-gradient(90deg, #3d6b1f, #86c559)",
                borderRadius: "2px", transition: "width 0.3s ease",
              }} />
            </div>
          )}

          {/* Items */}
          {checklist.map((item) => (
            <div key={item.id} style={{
              display: "flex", alignItems: "center", gap: "0.5rem",
              padding: "0.3rem 0.5rem", borderRadius: "0.375rem",
              background: item.done ? "rgba(61,107,31,0.04)" : "transparent",
              marginBottom: "2px",
            }}>
              <button
                data-testid={`checklist-toggle-${item.id}`}
                onClick={() => toggleChecklistItem(item.id)}
                style={{
                  width: "16px", height: "16px", flexShrink: 0,
                  border: `1.5px solid ${item.done ? "#3d6b1f" : "rgba(26,77,46,0.3)"}`,
                  borderRadius: "4px", background: item.done ? "#3d6b1f" : "transparent",
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "all 0.15s", padding: 0,
                }}
              >
                {item.done && (
                  <svg width="8" height="6" viewBox="0 0 9 7" fill="none">
                    <path d="M1 3.5L3.5 6L8 1" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
              <span style={{
                flex: 1, fontFamily: "'Manrope', sans-serif", fontSize: "0.85rem",
                color: item.done ? "#9ca3af" : "#374151",
                textDecoration: item.done ? "line-through" : "none",
                transition: "color 0.15s",
              }}>
                {item.text}
              </span>
              <button
                data-testid={`checklist-delete-${item.id}`}
                onClick={() => deleteChecklistItem(item.id)}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  color: "rgba(107,114,128,0.45)", padding: "2px",
                  display: "flex", transition: "color 0.15s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "#dc2626")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(107,114,128,0.45)")}
              >
                <X size={12} />
              </button>
            </div>
          ))}

          {/* Add item row */}
          <div style={{ display: "flex", gap: "0.4rem", marginTop: checklist.length ? "0.5rem" : "0" }}>
            <input
              data-testid="checklist-add-input"
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addChecklistItem(); }}
              placeholder="Add checklist item…"
              style={{
                flex: 1, padding: "0.4rem 0.6rem",
                background: "rgba(255,255,255,0.6)",
                border: "1px solid rgba(26,77,46,0.15)",
                borderRadius: "0.375rem",
                color: "#374151", fontSize: "0.82rem",
                fontFamily: "'Manrope', sans-serif", outline: "none",
                transition: "border-color 0.2s",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#3d6b1f")}
              onBlur={(e) => (e.target.style.borderColor = "rgba(26,77,46,0.15)")}
            />
            <button
              onClick={addChecklistItem}
              disabled={!newItem.trim()}
              style={{
                padding: "0.4rem 0.75rem",
                background: newItem.trim() ? "rgba(61,107,31,0.12)" : "rgba(26,77,46,0.05)",
                border: `1px solid ${newItem.trim() ? "rgba(61,107,31,0.35)" : "rgba(26,77,46,0.1)"}`,
                borderRadius: "0.375rem",
                cursor: newItem.trim() ? "pointer" : "not-allowed",
                color: newItem.trim() ? "#3d6b1f" : "#9ca3af",
                fontSize: "0.82rem", fontFamily: "'Manrope', sans-serif", fontWeight: 600,
                transition: "all 0.15s",
              }}
            >+ Add</button>
          </div>
        </div>

        {/* Created date */}
        <div style={{
          display: "flex", alignItems: "center", gap: "0.4rem",
          marginBottom: "1.25rem",
          padding: "0.5rem 0.75rem",
          background: "rgba(26,77,46,0.04)",
          border: "1px solid rgba(26,77,46,0.08)",
          borderRadius: "0.5rem",
        }}>
          <Clock size={13} color="#9ca3af" />
          <span style={{ fontFamily: "'Manrope', sans-serif", color: "#9ca3af", fontSize: "0.78rem" }}>
            Created {formatDate(task.created_at)}
          </span>
        </div>

        {/* Comments */}
        <div style={{ marginBottom: "1.25rem" }}>
          <label style={{ ...LABEL, display: "flex", alignItems: "center", gap: "0.3rem", marginBottom: "0.6rem" }}>
            <MessageSquare size={12} /> Comments
            {comments.length > 0 && (
              <span style={{
                background: "rgba(26,77,46,0.1)", color: "#1A4D2E",
                borderRadius: "2rem", padding: "0 0.45rem",
                fontSize: "0.7rem", fontWeight: 700,
              }}>
                {comments.length}
              </span>
            )}
          </label>

          {/* Comment list */}
          {comments.length > 0 && (
            <div style={{
              maxHeight: "220px", overflowY: "auto",
              display: "flex", flexDirection: "column", gap: "0.5rem",
              marginBottom: "0.65rem",
              paddingRight: "2px",
            }}>
              {comments.map((c) => (
                <div
                  key={c.id}
                  data-testid={`comment-${c.id}`}
                  style={{
                    display: "flex", gap: "0.6rem", alignItems: "flex-start",
                    padding: "0.6rem 0.75rem",
                    background: "rgba(26,77,46,0.04)",
                    border: "1px solid rgba(26,77,46,0.08)",
                    borderRadius: "0.6rem",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(26,77,46,0.07)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(26,77,46,0.04)")}
                >
                  {/* Avatar */}
                  <div style={{
                    width: "28px", height: "28px", borderRadius: "50%", flexShrink: 0,
                    background: "linear-gradient(135deg, #3d6b1f, #86c559)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <span style={{ color: "#fff", fontSize: "0.7rem", fontWeight: 700, fontFamily: "'Manrope', sans-serif" }}>
                      {(c.author_name || "?").charAt(0).toUpperCase()}
                    </span>
                  </div>

                  {/* Body */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "baseline", gap: "0.4rem", marginBottom: "0.2rem" }}>
                      <span style={{ fontFamily: "'Manrope', sans-serif", fontSize: "0.78rem", fontWeight: 700, color: "#1A4D2E" }}>
                        {c.author_name}
                      </span>
                      <span style={{ fontFamily: "'Manrope', sans-serif", fontSize: "0.7rem", color: "#9ca3af" }}>
                        {new Date(c.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                        {" "}
                        {new Date(c.created_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                      </span>
                    </div>
                    <p style={{
                      fontFamily: "'Manrope', sans-serif", fontSize: "0.85rem",
                      color: "#374151", margin: 0, lineHeight: 1.45,
                      whiteSpace: "pre-wrap", wordBreak: "break-word",
                    }}>
                      {c.text}
                    </p>
                  </div>

                  {/* Delete */}
                  <button
                    data-testid={`comment-delete-${c.id}`}
                    onClick={() => handleDeleteComment(c.id)}
                    style={{
                      background: "none", border: "none", cursor: "pointer",
                      color: "rgba(107,114,128,0.35)", padding: "2px",
                      display: "flex", flexShrink: 0, transition: "color 0.15s",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = "#dc2626")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(107,114,128,0.35)")}
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
              <div ref={commentsEndRef} />
            </div>
          )}

          {/* Add comment */}
          <div style={{ display: "flex", gap: "0.4rem", alignItems: "flex-end" }}>
            <textarea
              data-testid="comment-input"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handlePostComment(); }
              }}
              placeholder="Add a comment… (Enter to send, Shift+Enter for new line)"
              rows={2}
              style={{
                flex: 1, padding: "0.5rem 0.75rem",
                background: "rgba(255,255,255,0.6)",
                border: "1px solid rgba(26,77,46,0.15)",
                borderRadius: "0.5rem",
                color: "#374151", fontSize: "0.85rem",
                fontFamily: "'Manrope', sans-serif", outline: "none",
                resize: "none", lineHeight: 1.45,
                transition: "border-color 0.2s",
                boxSizing: "border-box",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#3d6b1f")}
              onBlur={(e) => (e.target.style.borderColor = "rgba(26,77,46,0.15)")}
            />
            <button
              data-testid="comment-submit-btn"
              onClick={handlePostComment}
              disabled={!commentText.trim() || postingComment}
              style={{
                padding: "0.55rem 0.75rem",
                background: commentText.trim() ? "#3d6b1f" : "rgba(26,77,46,0.08)",
                border: "none", borderRadius: "0.5rem",
                color: commentText.trim() ? "#fff" : "#9ca3af",
                cursor: commentText.trim() ? "pointer" : "not-allowed",
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "all 0.15s", flexShrink: 0,
              }}
              onMouseEnter={(e) => { if (commentText.trim()) e.currentTarget.style.background = "#4a7f25"; }}
              onMouseLeave={(e) => { if (commentText.trim()) e.currentTarget.style.background = "#3d6b1f"; }}
            >
              <Send size={15} />
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <p style={{
            fontFamily: "'Manrope', sans-serif", color: "#dc2626",
            fontSize: "0.8rem", marginBottom: "0.75rem",
            background: "rgba(220,38,38,0.07)", border: "1px solid rgba(220,38,38,0.2)",
            borderRadius: "0.4rem", padding: "0.4rem 0.75rem",
          }}>{error}</p>
        )}

        {/* Footer */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <button
            data-testid="task-detail-save-btn"
            onClick={handleSave}
            disabled={saving}
            style={{
              flex: 1, padding: "0.6rem 1rem",
              background: saving ? "rgba(61,107,31,0.45)" : "#3d6b1f",
              border: "none", borderRadius: "0.5rem",
              color: "#fff", fontFamily: "'Manrope', sans-serif",
              fontSize: "0.875rem", fontWeight: 600,
              cursor: saving ? "not-allowed" : "pointer",
              transition: "background 0.2s",
            }}
            onMouseEnter={(e) => { if (!saving) e.currentTarget.style.background = "#4a7f25"; }}
            onMouseLeave={(e) => { if (!saving) e.currentTarget.style.background = "#3d6b1f"; }}
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>
          <button
            data-testid="task-detail-cancel-btn"
            onClick={onClose}
            style={{
              padding: "0.6rem 1rem", background: "rgba(26,77,46,0.06)",
              border: "1px solid rgba(26,77,46,0.15)", borderRadius: "0.5rem",
              color: "#374151", fontFamily: "'Manrope', sans-serif",
              fontSize: "0.875rem", fontWeight: 600, cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            data-testid="task-detail-delete-btn"
            onClick={handleDelete}
            style={{
              padding: "0.6rem 0.875rem", background: "rgba(220,38,38,0.08)",
              border: "1px solid rgba(220,38,38,0.25)", borderRadius: "0.5rem",
              color: "#dc2626", fontFamily: "'Manrope', sans-serif",
              fontSize: "0.875rem", cursor: "pointer",
              display: "flex", alignItems: "center", gap: "0.3rem",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(220,38,38,0.15)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(220,38,38,0.08)"; }}
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
